// Sidebar.tsx
import React, { useContext, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Logo_Icon from "../assets/images/Logo_Icon.svg";
import Logo from "../assets/images/Logo_Color.svg";
import HomeIcon from "../assets/images/Sidebar/HomeIcon.svg";
import JobIcon from "../assets/images/Sidebar/JobIcon.svg";
import CandidateIcon from "../assets/images/Sidebar/CandidateIcon.svg";
import ContentIcon from "../assets/images/Sidebar/ContentIcon.svg";
import PostingIcon from "../assets/images/Sidebar/PostingIcon.svg";
import CampaignIcon from "../assets/images/Sidebar/CompaignIcon.svg";
import CandidateMenuIcon from "../assets/images/Sidebar/CandidateMeniUcon.svg";
import JobSummaryIcon from "../assets/images/Sidebar/JobSummaryIcon.svg";
import "../styles/Sidebar/sidebar.css";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarContext } from "../hooks/UseSidebar";
import { useAuth } from "../hooks/useAuth";
import { getCurrentSubscription } from "../helper/subscriptionHelper";

const NavIcon: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <span className="sidebar-icon-wrap">
    <img src={src} alt={alt} className="sidebar-icon" />
  </span>
);

const SidebarNavItem: React.FC<{
  label: string;
  icon: string;
  iconAlt: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, icon, iconAlt, active, onClick }) => (
  <Container className={`menu-item${active ? " is-active" : ""}`} onClick={onClick}>
    <Box className="menu-item-inner">
      <NavIcon src={icon} alt={iconAlt} />
      <Typography className="sidebar-lebel">{label}</Typography>
    </Box>
  </Container>
);

const Sidebar: React.FC<{
  onToggle: (expanded: boolean) => void;
  visible: boolean;
}> = ({ onToggle, visible }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [expanded, setExpanded] = React.useState(false);
  const { side, toggleSide, setSide } = useContext(SidebarContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:1200px)");
  const isTab = useMediaQuery(
    " (min-device-width: 900px) and (max-device-width: 1024px)"
  );
  const isDrawerMode =
    isMobile ||
    isTab ||
    (typeof window !== "undefined" && window.innerWidth <= 1300);
  const isSidebarOpen = isDrawerMode ? side : true;
  const isWide = expanded || (isDrawerMode && side);

  const sidebarRef = useRef<HTMLDivElement>(null);

  const MainRoute = () => {
    navigate("/");
  };

  const isActive = (path: string[]) => {
    return path.includes(location.pathname);
  };

  // Function to check if the current path is a child of the provided parent path
  const isChildActive = (parentPaths: string[]) => {
    return parentPaths.some((parentPath) =>
      location.pathname.startsWith(parentPath)
    );
  };

  const handleMenuItemClick = (url: string) => {
    navigate(url);

    if (isMobile || isTab || window.innerWidth <= 1300) {
      toggleSide();
    }

    if (!isMobile && !isTab && window.innerWidth > 1300 && expanded) {
      setTimeout(() => {
        setExpanded(false);
        onToggle(false);
      }, 100);
    }
  };

  const handleMouseEnter = () => {
    if (!isDrawerMode) {
      window.requestAnimationFrame(() => {
        setExpanded(true);
        onToggle(true);
      });
    }
  };

  const handleMouseLeave = () => {
    if (!isDrawerMode) {
      window.requestAnimationFrame(() => {
        setExpanded(false);
        onToggle(false);
      });
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      isDrawerMode &&
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node) &&
      side
    ) {
      toggleSide();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [side, isDrawerMode]);

  useEffect(() => {
    if (location.pathname === "/subscriptions") {
      setSide(false);
    }
  }, [location, setSide]);

  // Handle window resize to close sidebar on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1300 && side) {
        setSide(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [side, setSide]);

  return (
    <>
      <div
        ref={sidebarRef}
        className={`sidebar-item ${isSidebarOpen ? "visible" : "hidden"} ${
          isWide ? "is-expanded" : ""
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="sidebar-header" onClick={MainRoute}>
          <img
            src={Logo_Icon}
            alt="logo-icon"
            className="sidebar-logo-icon"
          />
          <img src={Logo} alt="logo" className="sidebar-logo-full" />
        </div>

        <div className="sidebar-nav">
          {isWide && <div className="sidebar-nav-label">Navigation</div>}
          <Grid
            container
            direction="column"
            justifyContent="flex-start"
            alignItems="stretch"
            className="Sidebar-Menu"
          >
            <SidebarNavItem
              label="Dashboard"
              icon={HomeIcon}
              iconAlt="HomeIcon"
              active={isActive(["/", "/hiring-manager"])}
              onClick={() =>
                handleMenuItemClick(
                  user?.status === 1 ? "/hiring-manager" : "/"
                )
              }
            />
            <SidebarNavItem
              label="Jobs"
              icon={JobIcon}
              iconAlt="JobIcon"
              active={
                isActive(["/jobs", "/hiring-manager/jobs"]) ||
                isChildActive(["/jobs", "/hiring-manager/jobs"])
              }
              onClick={() =>
                handleMenuItemClick(
                  user?.status === 1 ? "/hiring-manager/jobs" : "/jobs"
                )
              }
            />
            {user?.status !== 0 && (
              <SidebarNavItem
                label="Job Summary"
                icon={JobSummaryIcon}
                iconAlt="JobSummary"
                active={
                  isActive(["/job-summary", "/hiring-manager/job-summary"]) ||
                  isChildActive([
                    "/job-summary",
                    "/hiring-manager/job-summary",
                  ])
                }
                onClick={() =>
                  handleMenuItemClick("/hiring-manager/job-summary")
                }
              />
            )}
            <SidebarNavItem
              label="Candidates"
              icon={CandidateIcon}
              iconAlt="CandidateIcon"
              active={
                isActive(["/candidates", "/hiring-manager/candidates"]) ||
                isChildActive(["/candidates", "/hiring-manager/candidates"])
              }
              onClick={() =>
                handleMenuItemClick(
                  user?.status === 1
                    ? "/hiring-manager/candidates"
                    : "/candidates"
                )
              }
            />
            {user?.status !== 1 && (
              <SidebarNavItem
                label="Content Creation"
                icon={ContentIcon}
                iconAlt="ContentIcon"
                active={
                  isActive([
                    "/content-creation",
                    "/hiring-manager/content-creation",
                  ]) ||
                  isChildActive([
                    "/content-creation",
                    "/hiring-manager/content-creation",
                  ])
                }
                onClick={() => handleMenuItemClick("/content-creation")}
              />
            )}
            {user?.status !== 1 && (
              <SidebarNavItem
                label="Posting / Publishing"
                icon={PostingIcon}
                iconAlt="PostingIcon"
                active={
                  isActive([
                    "/posting-publishing",
                    "/hiring-manager/posting-publishing",
                  ]) ||
                  isChildActive([
                    "/posting-publishing",
                    "/hiring-manager/posting-publishing",
                  ])
                }
                onClick={async () => {
                  const { subscription, isActive: hasSubscription } =
                    await getCurrentSubscription();
                  if (!hasSubscription) {
                    navigate("/subscriptions");
                    return;
                  }
                  if (subscription?.type?.toLowerCase() === "basic") {
                    navigate("/update-subscriptions");
                    return;
                  }
                  handleMenuItemClick("/posting-publishing");
                }}
              />
            )}
            {user?.status !== 1 && (
              <SidebarNavItem
                label="Campaign"
                icon={CampaignIcon}
                iconAlt="CampaignIcon"
                active={
                  isActive(["/campaigns", "/hiring-manager/campaigns"]) ||
                  isChildActive(["/campaigns", "/hiring-manager/campaigns"])
                }
                onClick={() => handleMenuItemClick("/campaigns")}
              />
            )}
            <SidebarNavItem
              label="Candidate Menu"
              icon={CandidateMenuIcon}
              iconAlt="CandidateMenuIcon"
              active={
                isActive([
                  "/candidate-menu",
                  "/hiring-manager/candidate-menu",
                ]) ||
                isChildActive([
                  "/candidate-menu",
                  "/hiring-manager/candidate-menu",
                ])
              }
              onClick={() =>
                handleMenuItemClick(
                  user?.status === 1
                    ? "/hiring-manager/candidate-menu"
                    : "/candidate-menu"
                )
              }
            />
          </Grid>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
